import { useMemo, useRef } from "react"
import { motion, useInView } from "framer-motion"
import styled from "@emotion/styled"
import { css } from "@emotion/react"

import { slideUp } from "animations/slideUp"

import { useConsole } from "contexts/Console"

import { fullGrid } from "css/grid"
import getMediaQuery from "css/breakpoints"

import generateModularBlocks from "utils/generateModularBlocks"

import Image from "components/media/ImageCLD"
import FigCaption, { fullWidthInnerStyle } from "components/figcaption/FigCaption"

import { getGradient, modularTexts } from "./common"

const pseudoStyles = css`
  content: "";
  display: block;
  position: absolute;
  left: 0;
  width: 100%;
  height: 20vh;
  z-index: 0;
`

const Root = styled.article`
  ${fullGrid};
  grid-template-rows: [article figure] auto;
  position: relative;

  ::before {
    ${pseudoStyles}
    top: 0;
    background: rgb(var(--top-color));
    mask-image: linear-gradient(black, transparent);
    -webkit-mask-image: linear-gradient(black, transparent);
  }

  ::after {
    ${pseudoStyles}
    bottom: 0;
    background: rgb(var(--bot-color));
    mask-image: linear-gradient(transparent, black);
    -webkit-mask-image: linear-gradient(transparent, black);
  }
`

const TextContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  grid-row: article;
  grid-column: col 1 / span 6;
  text-align: start;
  position: relative;
  z-index: 2;

  .v-align-start & {
    align-self: start;
    padding-block-start: 50px;
  }
  .v-align-center & {
    align-self: center;
  }
  .v-align-end & {
    align-self: end;
    padding-block-end: 50px;
  }

  article.mob-v-align-start & {
    align-self: start;
    padding-block-start: 50px;
  }
  article.mob-v-align-center & {
    align-self: center;
  }
  article.mob-v-align-end & {
    align-self: end;
    padding-block-end: 50px;
  }

  ${getMediaQuery("m")} {
    article.v-align-start & {
      align-self: start;
      padding-block-start: 50px;
      padding-block-end: unset;
    }
    article.v-align-center & {
      align-self: center;
      padding-block-start: unset;
      padding-block-end: unset;
    }
    article.v-align-end & {
      align-self: end;
      padding-block-start: unset;
      padding-block-end: 50px;
    }

    .h-align-start & {
      grid-column: col 1 / span 6;
    }
    .h-align-center & {
      grid-column: col 4 / span 6;
    }
    .h-align-end & {
      grid-column: col 7 / span 6;
    }

    [dir="rtl"] .h-align-start & {
      grid-column: col 7 / span 6;
    }
    [dir="rtl"] .h-align-end & {
      grid-column: col 1 / span 6;
    }
  }

  ${getMediaQuery("l")} {
    .h-align-start & {
      grid-column: col 1 / span 5;
    }

    .h-align-end & {
      grid-column: col 8 / span 5;
    }

    [dir="rtl"] .h-align-start & {
      grid-column: col 8 / span 5;
    }
    [dir="rtl"] .h-align-end & {
      grid-column: col 1 / span 5;
    }
  }

  ${modularTexts}

  // A11y
  html.prefers-contrast & {
    color: black !important;
    position: relative;

    ::after {
      background: white;
      content: "";
      height: calc(100% + 30px);
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 20px);
      z-index: -1;
    }
  }
`

const Figure = styled.figure`
  grid-row: figure;
  grid-column: doc;
  ${fullGrid};

  & picture,
  & img {
    grid-column: doc;
    grid-row: 1;
  }

  & figcaption {
    ${fullWidthInnerStyle};
  }

  img {
    display: block;
    width: 100%;
    //dirty fix while waiting for ImageIM to receive width & height attrs
    min-height: 100px;
  }
`

export default function ArticleImage({
  className,
  vertical_align,
  vertical_align_mobile,
  horizontal_align,
  transition_gradient,
  image_cld: image,
  texts,
  title_gradient,
  chapo_gradient,
  fig_caption,
}) {
  const console = useConsole()

  const modularBlocks = useMemo(() => generateModularBlocks(texts, { console }), [])
  const prefersReducedMotion = process.browser && document.documentElement.classList.contains("prefers-reduced-motion")
  const titleGradient = title_gradient?.colors ? getGradient(title_gradient.colors) : null
  const chapoGradient = chapo_gradient?.colors ? getGradient(chapo_gradient.colors) : null
  const textContRef = useRef()
  const isInView = useInView(textContRef, { margin: "100% 0% 0% 0%" })

  console.verbose("\n•••••••• ARTICLE IMAGE", fig_caption)

  return (
    <Root
      style={{
        "--top-color": transition_gradient.top_color,
        "--bot-color": transition_gradient.bottom_color,
        "--title-gradient": titleGradient,
        "--chapo-gradient": chapoGradient,
      }}
      className={`v-align-${vertical_align.align} h-align-${horizontal_align.align} ${className}${
        vertical_align_mobile?.align ? ` mob-v-align-${vertical_align_mobile.align}` : ""
      }`}
    >
      <Figure>
        <Image {...image} sizes='100vw' />
        {fig_caption?.caption?.length ? <FigCaption {...fig_caption} /> : null}
      </Figure>

      <TextContainer ref={textContRef} variants={slideUp} initial='hidden' animate={prefersReducedMotion ? "show" : isInView ? "show" : "hide"}>
        {modularBlocks}
      </TextContainer>
    </Root>
  )
}
